import { forwardRef } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center rounded-sm text-sm font-medium transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary-300)] focus-visible:ring-offset-2 disabled:opacity-40 disabled:pointer-events-none",
  {
    variants: {
      variant: {
        default: "border border-[var(--primary-600)] bg-[var(--primary-600)] text-white hover:border-[var(--primary-400)] hover:bg-[var(--primary-400)] shadow-[0_2px_0_rgba(0,0,0,0.04)]",
        destructive: "border border-[var(--error)] bg-[var(--error)] text-white hover:bg-red-500",
        outline: "border border-[#d9d9d9] bg-white text-[var(--foreground)] hover:border-[var(--primary-500)] hover:text-[var(--primary-600)]",
        secondary: "bg-[var(--muted)] text-[var(--foreground)] hover:bg-[var(--primary-100)]",
        ghost: "text-[var(--foreground)] hover:bg-[var(--muted)]",
        accent: "border border-[var(--accent-500)] bg-[var(--accent-500)] text-white hover:bg-[var(--accent-600)]",
      },
      size: {
        default: "h-9 px-3 text-sm",
        sm: "h-8 px-2.5 text-xs",
        lg: "h-11 px-5 text-base",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, ...props }, ref) => (
    <button ref={ref} className={cn(buttonVariants({ variant, size }), className)} {...props} />
  ),
);
Button.displayName = "Button";
